<script>
import moment from "moment";
import { AuditStatusEnum, auditStatusList, registrationSourceList, findLabel, downloadBlob } from "../help";
import {
  getTechActivityList,
  getAuditList,
  getAuditQuota,
  auditSignUp,
  auditPrecheck,
  exportAuditList,
} from "@/api/techInnoActivities";

export default {
  name: "TechInnoActivityAudit",
  data() {
    return {
      moment,
      AuditStatusEnum,
      auditStatusList,
      // 活动下拉数据
      activityOptions: [],
      // 搜索条件
      searchForm: {
        // 活动（不选则查全部）
        activityId: undefined,
        // 手机号
        phone: "",
        // 姓名
        userName: "",
        // 公司
        companyName: "",
        // 审核状态（默认待审核）
        auditStatus: AuditStatusEnum.WAIT_AUDIT,
      },
      columns: [
        { title: "活动名称", dataIndex: "activityName", key: "activityName", width: 180, ellipsis: true },
        { title: "板块", dataIndex: "sectionName", key: "sectionName", width: 110 },
        { title: "活动类型", dataIndex: "typeName", key: "typeName", width: 110 },
        { title: "姓名", dataIndex: "userName", key: "userName", width: 110 },
        { title: "企业", dataIndex: "companyName", key: "companyName", width: 170, ellipsis: true },
        { title: "手机号", dataIndex: "phone", key: "phone", width: 120 },
        { title: "报名身份", dataIndex: "identityName", key: "identityName", width: 110, ellipsis: true, customRender: (text) => text || "--" },
        {
          title: "来源",
          key: "source",
          width: 110,
          ellipsis: true,
          customRender: (text, record) => findLabel(registrationSourceList, record.source) || "--",
        },
        { title: "报名时间", dataIndex: "createTime", key: "createTime", width: 170 },
        {
          title: "审核状态",
          dataIndex: "auditStatusStr",
          key: "auditStatusStr",
          width: 110,
          customRender: (text, record) =>
            text || findLabel(auditStatusList, record.auditStatus) || "--",
        },
        { title: "操作", key: "action", width: 170, fixed: "right", scopedSlots: { customRender: "action" } },
      ],
      dataInfo: [],
      listLoading: false,
      // 名额信息（选定活动后展示，接口实时计算）
      quota: null,
      // 多选：选中报名记录 id（跨筛选/翻页保持，仅成功批量操作后清空）
      selectedRowKeys: [],
      // 选中记录基础信息缓存（id -> {userName, phone, activityId, activityName}）
      recordInfoMap: {},
      // 跨页全选 loading（表头全选触发，拉取当前筛选下全部待审核）
      selectAllLoading: false,
      // 单次批量上限
      batchLimit: 100,
      // 全选模式：批量审核不传 ids，按当前筛选条件由服务端圈定全部待审核（4.3 全选模式）
      selectAllMode: false,
      // 批量审核动作选择弹窗（通过/驳回二选一）
      actionVisible: false,
      // 二次确认弹窗：action = pass / reject
      confirmVisible: false,
      confirmAction: "pass",
      confirmIds: [],
      // 名额校验结果 [{ activityId, activityName, remaining, count, exceeded }]
      confirmQuotas: [],
      // 预检接口异常标记（不阻断提交，服务端逐条校验兜底）
      quotaPrecheckFailed: false,
      confirmLoading: false,
      // 结果反馈弹窗
      resultVisible: false,
      resultSuccessCount: 0,
      resultFailCount: 0,
      resultFailList: [],
      // 导出 loading
      exportLoading: false,
      pagination: {
        pageSizeOptions: ["10", "20", "30", "50", "100"],
        showSizeChanger: true,
        current: 1,
        pageSize: 10,
        total: 0,
        showQuickJumper: true,
        showTotal: (total) => `共 ${total} 条`,
      },
    };
  },
  computed: {
    // 行选择：仅待审核可选（其余置灰）；表头全选 = 当前筛选下全部待审核（跨页）
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onSelect: (record, selected) => this.handleRowSelect(record, selected),
        onSelectAll: (selected) => this.handleSelectAll(selected),
        getCheckboxProps: (record) => ({
          props: { disabled: record.auditStatus !== AuditStatusEnum.WAIT_AUDIT },
        }),
        columnWidth: 50,
      };
    },
    // 已选超出单次批量上限
    overLimit() {
      return this.selectedRowKeys.length > this.batchLimit;
    },
    // 全选模式前置校验：审核状态须为 待审核 或 全部（后端全选模式 auditStatus 仅允许空或 0）
    canSelectAllMode() {
      return (
        this.searchForm.auditStatus === AuditStatusEnum.WAIT_AUDIT ||
        this.searchForm.auditStatus === ""
      );
    },
    isSingleConfirm() {
      return this.confirmIds.length === 1;
    },
    confirmText() {
      const action = this.confirmAction === "pass" ? "通过" : "拒绝";
      if (this.selectAllMode) {
        return `将对当前筛选条件下的全部待审核报名申请一次性${action}（含未加载分页数据），操作后将向报名人推送审核结果通知，该操作不可撤销`;
      }
      return `将一次性${action} ${this.confirmIds.length} 条活动报名申请，操作后将向报名人推送审核结果通知，该操作不可撤销`;
    },
  },
  watch: {
    // 切换审核状态后圈定范围变化，清空已选并退出全选（跨状态的旧选择不再参与批量）
    "searchForm.auditStatus"() {
      this.selectedRowKeys = [];
      this.selectAllMode = false;
    },
  },
  created() {
    this.getActivityOptions();
    this.getList();
  },
  mounted() {
    // 宽屏下表格无横向滚动，取消操作列固定，避免固定列 overlay 产生空白列
    this.handleResize = () => {
      const wide = this.$el.clientWidth >= 1610;
      const cols = this.columns;
      cols[cols.length - 1].fixed = wide ? "" : "right";
    };
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    /**
     * @Description：活动下拉数据
     * @Author cxd
     * @Date 2026/10/26
     */
    async getActivityOptions() {
      try {
        const res = await getTechActivityList({ page: 1, size: 999 });
        if (String(res.code) === "200") {
          this.activityOptions = (res.data && res.data.items) || [];
        }
      } catch (e) {
        this.activityOptions = [];
      }
    },
    /**
     * @Description：报名审核列表（接口 page 从 1 开始）
     * @Author cxd
     * @Date 2026/10/26
     */
    async getList() {
      const params = {
        activityId: this.searchForm.activityId,
        phone: this.searchForm.phone,
        userName: this.searchForm.userName,
        companyName: this.searchForm.companyName,
        // “全部”不传 auditStatus（约定同 4.5 导出：不传=全部审核状态；后端 4.1 当前不传仍默认待审核，待后端放开）
        auditStatus: this.searchForm.auditStatus === "" ? undefined : this.searchForm.auditStatus,
        page: this.pagination.current,
        size: this.pagination.pageSize,
      };
      this.listLoading = true;
      try {
        const res = await getAuditList(params);
        if (String(res.code) === "200") {
          this.dataInfo = (res.data && res.data.items) || [];
          this.pagination.total = (res.data && res.data.total) || 0;
        } else {
          this.$message.error(res.msg || "查询失败");
        }
      } catch (e) {
        this.$message.error("查询失败");
      } finally {
        this.listLoading = false;
        this.fetchQuota();
      }
    },
    /**
     * @Description：名额信息（选定具体活动后拉取；未选活动或接口异常时不展示）
     * @Author cxd
     * @Date 2026/10/28
     */
    async fetchQuota() {
      if (!this.searchForm.activityId) {
        this.quota = null;
        return;
      }
      try {
        const res = await getAuditQuota(this.searchForm.activityId);
        this.quota = String(res.code) === "200" ? res.data : null;
      } catch (e) {
        this.quota = null;
      }
    },
    /**
     * @Description：查询
     * @Author cxd
     * @Date 2026/10/26
     */
    search() {
      this.pagination.current = 1;
      this.getList();
    },
    /**
     * @Description：重置
     * @Author cxd
     * @Date 2026/10/26
     */
    resetSearchForm() {
      this.searchForm = {
        activityId: undefined,
        phone: "",
        userName: "",
        companyName: "",
        auditStatus: AuditStatusEnum.WAIT_AUDIT,
      };
      this.pagination.current = 1;
      this.pagination.pageSize = 10;
      this.getList();
    },
    /**
     * @Description：分页
     * @Author cxd
     * @Date 2026/10/26
     */
    pageChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getList();
    },
    /**
     * @Description：单行勾选/取消（跨页、跨筛选保持已选）
     * @Author cxd
     * @Date 2026/10/31
     */
    handleRowSelect(record, selected) {
      // 手动勾选行时退出全选模式，两套选中互斥
      if (this.selectAllMode) {
        this.selectAllMode = false;
      }
      this.cacheRecord(record);
      if (selected) {
        if (!this.selectedRowKeys.includes(record.id)) {
          this.selectedRowKeys = [...this.selectedRowKeys, record.id];
        }
      } else {
        this.selectedRowKeys = this.selectedRowKeys.filter((key) => key !== record.id);
      }
    },
    /**
     * @Description：表头全选：勾选 = 拉取当前筛选条件下全部待审核（含未加载分页）合并进已选；取消 = 清空已选
     * @Author cxd
     * @Date 2026/10/31
     */
    async handleSelectAll(selected) {
      // 手动操作表格全选框时同样退出全选模式
      if (this.selectAllMode) {
        this.selectAllMode = false;
      }
      if (!selected) {
        this.selectedRowKeys = [];
        return;
      }
      this.selectAllLoading = true;
      try {
        const records = await this.fetchAllPendingRecords();
        records.forEach((record) => this.cacheRecord(record));
        const merged = [...new Set([...this.selectedRowKeys, ...records.map((r) => r.id)])];
        this.selectedRowKeys = merged;
        if (!merged.length) {
          this.$message.info("当前筛选条件下没有待审核记录");
        } else if (merged.length > this.batchLimit) {
          this.$message.warning(
            `已选 ${merged.length} 条，超过单次批量上限 ${this.batchLimit} 条，请缩小筛选范围或减少选择`
          );
        }
      } catch (e) {
        this.$message.error("全选失败，请重试");
      } finally {
        this.selectAllLoading = false;
      }
    },
    /**
     * @Description：按当前筛选条件分页拉取全部待审核记录（不影响列表展示）
     * @Author cxd
     * @Date 2026/10/31
     */
    async fetchAllPendingRecords() {
      const size = 200;
      const maxPages = 50; // 兜底防死循环（最多 1 万条）
      let page = 1;
      let total = Infinity;
      const records = [];
      while (records.length < total && page <= maxPages) {
        const res = await getAuditList({
          activityId: this.searchForm.activityId,
          phone: this.searchForm.phone,
          userName: this.searchForm.userName,
          companyName: this.searchForm.companyName,
          auditStatus: this.searchForm.auditStatus,
          page,
          size,
        });
        if (String(res.code) !== "200") {
          throw new Error(res.msg || "查询失败");
        }
        const items = (res.data && res.data.items) || [];
        total = (res.data && res.data.total) || 0;
        items
          .filter((item) => item.auditStatus === AuditStatusEnum.WAIT_AUDIT)
          .forEach((item) => records.push(item));
        if (!items.length) break;
        page += 1;
      }
      return records;
    },
    /**
     * @Description：缓存选中记录基础信息（结果反馈里展示失败明细用）
     * @Author cxd
     * @Date 2026/10/31
     */
    cacheRecord(record) {
      if (!record || record.id == null) return;
      this.recordInfoMap[record.id] = {
        userName: record.userName,
        phone: record.phone,
        activityId: record.activityId,
        activityName: record.activityName,
      };
    },
    /**
     * @Description：批量操作完成后清理已处理记录的选中与缓存
     * @Author cxd
     * @Date 2026/10/31
     */
    removeProcessed(ids) {
      const done = new Set(ids);
      this.selectedRowKeys = this.selectedRowKeys.filter((key) => !done.has(key));
      done.forEach((id) => delete this.recordInfoMap[id]);
    },
    /**
     * @Description：全选开关：开启后批量审核走全选模式（不传 ids，按当前筛选条件由服务端圈定全部待审核）
     * @Author cxd
     * @Date 2026/10/31
     */
    toggleSelectAllMode() {
      if (!this.selectAllMode && !this.canSelectAllMode) {
        this.$message.error("批量审核仅支持待审核状态，请将审核状态筛选切换为待审核后操作");
        return;
      }
      this.selectAllMode = !this.selectAllMode;
      if (this.selectAllMode) {
        // 开启全选后批量审核不依赖勾选，清空行选择避免两套选中混用
        this.selectedRowKeys = [];
        this.$message.info("已开启全选：批量审核将按当前筛选条件圈定全部待审核");
      }
    },
    /**
     * @Description：批量审核入口：弹出动作选择（通过/驳回二选一），选择后再走统一确认弹窗
     * @Author cxd
     * @Date 2026/10/31
     */
    openBatchChooser() {
      if (this.selectAllMode) {
        if (!this.canSelectAllMode) {
          this.$message.error("批量审核仅支持待审核状态，请将审核状态筛选切换为待审核后操作");
          return;
        }
        this.actionVisible = true;
        return;
      }
      const ids = [...this.selectedRowKeys];
      if (!ids.length) return;
      if (ids.length > this.batchLimit) {
        this.$message.warning(
          `单次批量审核最多 ${this.batchLimit} 条，当前已选 ${ids.length} 条，请减少选择`
        );
        return;
      }
      this.actionVisible = true;
    },
    /**
     * @Description：批量通过 / 批量驳回入口（超上限阻断；全选模式透传筛选条件）
     * @Author cxd
     * @Date 2026/10/31
     */
    openBatchConfirm(action) {
      this.actionVisible = false;
      if (this.selectAllMode) {
        if (!this.canSelectAllMode) {
          this.$message.error("批量审核仅支持待审核状态，请将审核状态筛选切换为待审核后操作");
          return;
        }
        this.openConfirm(action, []);
        return;
      }
      const ids = [...this.selectedRowKeys];
      if (!ids.length) return;
      if (ids.length > this.batchLimit) {
        this.$message.warning(
          `单次批量审核最多 ${this.batchLimit} 条，当前已选 ${ids.length} 条，请减少选择`
        );
        return;
      }
      this.openConfirm(action, ids);
    },
    /**
     * @Description：打开统一二次确认弹窗（单条审核沿用；通过时按活动实时拉取剩余名额）
     * @Author cxd
     * @Date 2026/10/31
     */
    async openConfirm(action, ids) {
      this.confirmAction = action;
      this.confirmIds = [...ids];
      this.confirmQuotas = [];
      this.confirmVisible = true;
      if (action === "pass") {
        await this.fetchConfirmQuotas();
      }
    },
    /**
     * @Description：构造审核圈定入参（预检与提交共用；withOperate=false 时不带动作字段）
     * @Author cxd
     * @Date 2026/10/31
     */
    buildAuditPayload(withOperate) {
      const payload = this.selectAllMode
        ? {
            // 全选模式：ids 为空，透传当前筛选条件由服务端圈定全部待审核（auditStatus 仅允许空或 0）
            ids: [],
            activityId: this.searchForm.activityId,
            phone: this.searchForm.phone,
            userName: this.searchForm.userName,
            companyName: this.searchForm.companyName,
            // “全部”筛选=空（后端仅允许空或 0，空即不传）
            auditStatus: this.searchForm.auditStatus === "" ? undefined : this.searchForm.auditStatus,
          }
        : { ids: [...this.confirmIds] };
      if (withOperate) {
        payload.operateStatus =
          this.confirmAction === "pass" ? AuditStatusEnum.AUDIT_PASS : AuditStatusEnum.AUDIT_REJECT;
      }
      return payload;
    },
    /**
     * @Description：确认弹窗名额预检（4.4，与批量审核同源圈定；按活动分组返回 已选/剩余/超额）
     * @Author cxd
     * @Date 2026/10/31
     */
    async fetchConfirmQuotas() {
      this.quotaPrecheckFailed = false;
      try {
        const res = await auditPrecheck(this.buildAuditPayload(false));
        if (String(res.code) === "200" && Array.isArray(res.data)) {
          this.confirmQuotas = res.data.map((g) => ({
            activityId: g.activityId,
            activityName: g.activityName,
            remaining: g.remaining,
            count: g.selectedCount,
            exceeded: g.exceededCount || 0,
          }));
        } else {
          // 空集/超上限等业务校验失败：提示并关闭弹窗
          this.confirmVisible = false;
          this.$message.error(res.msg || "没有可审核的待审核记录");
        }
      } catch (e) {
        // 预检异常不阻断提交，服务端逐条校验兜底
        this.quotaPrecheckFailed = true;
        this.confirmQuotas = [];
      }
    },
    /**
     * @Description：确认弹窗提交（批量/单条共用；服务端逐条校验兜底）
     * @Author cxd
     * @Date 2026/10/31
     */
    async submitBatchAudit() {
      this.confirmLoading = true;
      const payload = this.buildAuditPayload(true);
      const resultIds = this.selectAllMode ? [] : [...this.confirmIds];
      try {
        const res = await auditSignUp(payload);
        if (String(res.code) === "200") {
          this.handleAuditResult(resultIds, res.data);
          this.confirmVisible = false;
          this.removeProcessed(resultIds);
          this.getList();
        } else {
          this.$message.error(res.msg || "操作失败");
        }
      } catch (e) {
        this.$message.error("操作失败");
      } finally {
        this.confirmLoading = false;
      }
    },
    /**
     * @Description：审核结果反馈：优先解析逐条结果（成功n/失败m+原因），整批成功时仅 message
     * @Author cxd
     * @Date 2026/10/31
     */
    handleAuditResult(ids, data) {
      const d = data || {};
      const failArr =
        d.failures ||
        d.failedItems ||
        d.failList ||
        (Array.isArray(d.results)
          ? d.results.filter((r) => r && (r.success === false || r.status === 2))
          : null);
      const successCount = d.successCount != null ? Number(d.successCount) : null;
      const failCount =
        d.failCount != null ? Number(d.failCount) : failArr ? failArr.length : null;
      if (successCount != null || (failArr && failArr.length)) {
        this.resultSuccessCount =
          successCount != null
            ? successCount
            : Math.max((ids ? ids.length : 0) - (failCount || 0), 0);
        this.resultFailCount = failCount != null ? failCount : (failArr || []).length;
        this.resultFailList = (failArr || []).map((f) => {
          const info = (this.recordInfoMap && this.recordInfoMap[f.id]) || {};
          return {
            userName: f.userName || info.userName || "未知用户",
            phone: f.phone || info.phone || "",
            reason: f.reason || f.msg || f.failReason || "失败",
          };
        });
        this.resultVisible = true;
      } else {
        this.$message.success(ids && ids.length ? `操作成功：成功 ${ids.length} 条` : "操作成功");
      }
    },
    /**
     * @Description：审核列表导出（按当前筛选条件；条件全空=全量）
     * @Author cxd
     * @Date 2026/10/31
     */
    async handleExport() {
      this.exportLoading = true;
      try {
        const res = await exportAuditList({
          activityId: this.searchForm.activityId,
          phone: this.searchForm.phone,
          userName: this.searchForm.userName,
          companyName: this.searchForm.companyName,
          // “全部”不传 auditStatus（4.5：不传=全部审核状态）
          auditStatus: this.searchForm.auditStatus === "" ? undefined : this.searchForm.auditStatus,
        });
        downloadBlob(res, `报名审核-${moment().format("YYYYMMDDHHmmss")}.xlsx`);
      } catch (e) {
        this.$message.error("导出失败，请稍后重试");
      } finally {
        this.exportLoading = false;
      }
    },
    /**
     * @Description：审核通过（走统一确认弹窗，弹窗内实时校验剩余名额）
     * @Author cxd
     * @Date 2026/10/26
     */
    handlePass(record) {
      this.cacheRecord(record);
      this.openConfirm("pass", [record.id]);
    },
    /**
     * @Description：审核驳回（走统一确认弹窗；驳回后用户可重新报名）
     * @Author cxd
     * @Date 2026/10/26
     */
    handleReject(record) {
      this.cacheRecord(record);
      this.openConfirm("reject", [record.id]);
    },
  },
};
</script>

<template>
  <div class="tech-inno-audit">
    <ds-header title="报名审核"></ds-header>
    <a-form layout="inline" style="margin-bottom: 10px">
      <a-form-item label="活动：">
        <a-select
          v-model="searchForm.activityId"
          placeholder="请选择活动"
          style="width: 220px"
          allowClear
          show-search
          option-filter-prop="children"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option
            v-for="item in activityOptions"
            :key="item.id"
            :value="String(item.id)"
          >
            {{ item.name }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="手机号：">
        <a-input
          v-model.trim="searchForm.phone"
          placeholder="请输入手机号"
          style="width: 160px"
        />
      </a-form-item>
      <a-form-item label="姓名：">
        <a-input v-model.trim="searchForm.userName" placeholder="请输入姓名" style="width: 130px" />
      </a-form-item>
      <a-form-item label="公司：">
        <a-input
          v-model.trim="searchForm.companyName"
          placeholder="请输入公司"
          style="width: 160px"
        />
      </a-form-item>
      <a-form-item label="审核状态：">
        <a-select
          v-model="searchForm.auditStatus"
          placeholder="请选择"
          style="width: 150px"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        >
          <a-select-option value=""> 全部 </a-select-option>
          <a-select-option v-for="item in auditStatusList" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <span>
        <a-button type="primary" style="margin-top: 3px" icon="search" @click="search">
          查询
        </a-button>
        <a-button style="margin-left: 15px" icon="redo" @click="resetSearchForm">重置</a-button>
      </span>
    </a-form>
    <div style="margin-top: 15px">
      <!-- 批量操作：全选开关 + 批量审核；手动勾选模式下未选中置灰，单次批量上限 100 条 -->
      <div class="batch-bar">
        <a-button :type="selectAllMode ? 'primary' : 'default'" @click="toggleSelectAllMode">
          全选
        </a-button>
        <a-button
          style="margin-left: 15px"
          type="primary"
          :disabled="!selectAllMode && (selectedRowKeys.length === 0 || overLimit)"
          @click="openBatchChooser"
        >
          批量审核{{
            !selectAllMode && selectedRowKeys.length ? `（${selectedRowKeys.length}）` : ""
          }}
        </a-button>
        <a-button
          style="margin-left: 15px"
          icon="download"
          :loading="exportLoading"
          @click="handleExport"
        >
          导出
        </a-button>
        <span class="batch-tip" :class="{ danger: !selectAllMode && overLimit }">
          {{
            selectAllMode
              ? "全选模式：按当前筛选条件圈定"
              : `已选 ${selectedRowKeys.length} 条，单次批量上限 ${batchLimit} 条`
          }}
        </span>
      </div>
      <!-- 名额信息（选定活动后展示；剩余为 0 时审核通过置灰，并发以服务端校验为准） -->
      <div v-if="quota" class="quota-bar">
        <span class="quota-name">{{ quota.activityName }}</span>
        <span>规模：{{ quota.capacity }}</span>
        <span>已占：{{ quota.occupied }}</span>
        <span>剩余：{{ quota.remaining }}</span>
        <span>待审核：{{ quota.pendingCount }}</span>
        <a-tag v-if="quota.remaining === 0" color="red">已满</a-tag>
      </div>
      <a-table
        :rowKey="(record) => record.id"
        :columns="columns"
        :data-source="dataInfo"
        :loading="listLoading || selectAllLoading"
        :row-selection="rowSelection"
        :pagination="pagination.total ? pagination : false"
        :scroll="{ x: 1590 }"
        bordered
        @change="pageChange"
      >
        <span slot="action" slot-scope="text, record" class="action">
          <template v-if="record.auditStatus === AuditStatusEnum.WAIT_AUDIT">
            <a-button
              type="link"
              class="btn"
              :disabled="quota && quota.remaining === 0"
              @click="handlePass(record)"
            >
              审核通过
            </a-button>
            <a-button type="link" class="btn" @click="handleReject(record)"> 审核驳回 </a-button>
          </template>
          <span v-else>--</span>
        </span>
      </a-table>
    </div>
    <!-- 批量审核动作选择弹窗（通过/驳回二选一，选择后进入统一确认弹窗） -->
    <a-modal
      :visible="actionVisible"
      title="批量审核"
      @cancel="actionVisible = false"
    >
      <template slot="footer">
        <a-button type="danger" @click="openBatchConfirm('reject')"> 审核驳回 </a-button>
        <a-button type="primary" @click="openBatchConfirm('pass')"> 审核通过 </a-button>
      </template>
      <p v-if="selectAllMode" class="select-all-line">
        <a-tag color="blue">全选</a-tag>
        将对当前筛选条件下的全部待审核报名执行批量审核
      </p>
      <p v-else>已选 {{ selectedRowKeys.length }} 条待审核的报名申请，请选择审核动作。</p>
      <p class="chooser-tip">
        通过时将实时校验活动剩余名额；操作后向报名人推送审核结果通知，不可撤销。
      </p>
    </a-modal>
    <!-- 统一二次确认弹窗（单条/批量共用；通过时经 4.4 预检按活动展示 已选/剩余名额，超额不阻断、提交后逐条失败） -->
    <a-modal
      :visible="confirmVisible"
      :title="
        (confirmAction === 'pass' ? '批量通过确认' : '批量驳回确认') +
        (isSingleConfirm ? '（单条）' : '')
      "
      :confirm-loading="confirmLoading"
      :ok-text="confirmAction === 'pass' ? '确认通过' : '确认拒绝'"
      cancel-text="取消"
      :maskClosable="false"
      @ok="submitBatchAudit"
      @cancel="confirmVisible = false"
    >
      <p>{{ confirmText }}</p>
      <p v-if="selectAllMode" class="select-all-line">
        <a-tag color="blue">全选模式</a-tag>
        按当前筛选条件圈定全部待审核记录
      </p>
      <div v-if="confirmAction === 'pass'" class="quota-lines">
        <div
          v-for="q in confirmQuotas"
          :key="q.activityId != null ? q.activityId : q.activityName"
          class="quota-line"
          :class="{ danger: q.exceeded > 0 }"
        >
          {{ q.activityName }}：已选 {{ q.count }} 条 / 剩余名额 {{ q.remaining
          }}<span v-if="q.exceeded > 0">
            ，选中的数据超过剩余名额 {{ q.exceeded }} 条，通过时超出部分将失败</span
          >
        </div>
        <div v-if="quotaPrecheckFailed" class="quota-line">
          名额预检失败，提交时以服务端逐条校验为准
        </div>
      </div>
    </a-modal>
    <!-- 批量结果反馈：成功 n、失败 m 及逐条失败原因 -->
    <a-modal
      :visible="resultVisible"
      :title="confirmAction === 'pass' ? '批量通过结果' : '批量驳回结果'"
      :footer="null"
      @cancel="resultVisible = false"
    >
      <p>
        成功 <b class="ok-count">{{ resultSuccessCount }}</b> 条、失败
        <b :class="{ 'fail-count': resultFailCount > 0 }">{{ resultFailCount }}</b> 条
      </p>
      <div v-if="resultFailList.length" class="fail-list">
        <div v-for="(item, index) in resultFailList" :key="index" class="fail-item">
          {{ item.userName }}<template v-if="item.phone">（{{ item.phone }}）</template
          >：{{ item.reason }}
        </div>
      </div>
    </a-modal>
  </div>
</template>

<style scoped lang="scss">
.batch-bar {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .batch-tip {
    margin-left: 15px;
    color: #999;
    font-size: 12px;

    &.danger {
      color: #f5222d;
    }
  }
}

.chooser-tip {
  color: #999;
  font-size: 12px;
}

.select-all-line {
  .ant-tag {
    margin-right: 8px;
  }
}

.quota-lines {
  margin-top: 4px;

  .quota-line {
    line-height: 24px;

    &.danger {
      color: #f5222d;
    }
  }
}

.ok-count {
  color: #52c41a;
}

.fail-count {
  color: #f5222d;
}

.fail-list {
  max-height: 240px;
  overflow: auto;
  padding: 8px 12px;
  background: #fff7f0;
  border: 1px solid #ffd8c4;
  border-radius: 4px;

  .fail-item {
    line-height: 24px;
  }
}

.quota-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 16px;
  margin-bottom: 10px;
  background: #f0f7ff;
  border: 1px solid #d6e8ff;
  border-radius: 4px;

  .quota-name {
    font-weight: 600;
  }
}

.action {
  // 操作按钮不允许换行，避免窄列下串行
  white-space: nowrap;
}

.btn {
  padding: 0;
}
</style>
